import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionCard = ({ subscription, onUpgrade, onManage }) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });

  // Check if current user is a practitioner
  const userRole = localStorage.getItem('userRole');
  
  // Don't render anything if user is not a practitioner
  if (userRole !== 'practitioner') {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    let formattedValue = value;

    // Format card number with spaces
    if (name === 'cardNumber') {
      formattedValue = value?.replace(/\s/g, '')?.replace(/(.{4})/g, '$1 ')?.trim();
      if (formattedValue?.length > 19) return;
    }

    // Format expiry date
    if (name === 'expiryDate') {
      formattedValue = value?.replace(/\D/g, '')?.replace(/(\d{2})(\d)/, '$1/$2');
      if (formattedValue?.length > 5) return;
    }

    // Format CVV
    if (name === 'cvv') {
      formattedValue = value?.replace(/\D/g, '');
      if (formattedValue?.length > 3) return;
    }

    setPaymentData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleUpgrade = async (e) => {
    e?.preventDefault();
    setIsProcessing(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    onUpgrade();
    setShowPaymentModal(false);
    setIsProcessing(false);
    setPaymentData({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: ''
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <>
      <div className="bg-card rounded-lg p-6 shadow-organic">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Practitioner Subscription
            </h3>
            <p className="text-sm text-muted-foreground">
              Manage your premium practitioner features and business visibility
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            subscription?.isActive 
              ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
          }`}>
            {subscription?.isActive ? 'Premium Active' : 'Basic Plan'}
          </div>
        </div>

        {subscription?.isActive ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-success/5 border border-success/20 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center mr-3">
                  <Icon name="Check" size={20} color="var(--color-success)" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Premium Practitioner Features</p>
                  <p className="text-sm text-muted-foreground">
                    Enhanced listings, priority placement, and advanced analytics
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-success">£29.99/month</p>
                <p className="text-xs text-muted-foreground">
                  Next billing {formatDate(subscription?.renewalDate)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center mb-2">
                  <Icon name="Zap" size={16} className="mr-2 text-primary" />
                  <span className="text-sm font-medium text-foreground">Priority Listings</span>
                </div>
                <p className="text-xs text-muted-foreground">Your services appear at the top of search results</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center mb-2">
                  <Icon name="BarChart" size={16} className="mr-2 text-primary" />
                  <span className="text-sm font-medium text-foreground">Advanced Analytics</span>
                </div>
                <p className="text-xs text-muted-foreground">Detailed insights on client engagement and bookings</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center mb-2">
                  <Icon name="Star" size={16} className="mr-2 text-primary" />
                  <span className="text-sm font-medium text-foreground">Enhanced Profile</span>
                </div>
                <p className="text-xs text-muted-foreground">Rich media, detailed service descriptions, and testimonials</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center mb-2">
                  <Icon name="MessageSquare" size={16} className="mr-2 text-primary" />
                  <span className="text-sm font-medium text-foreground">Direct Messaging</span>
                </div>
                <p className="text-xs text-muted-foreground">Communicate directly with potential clients</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div>
                <p className="text-sm text-foreground">
                  <strong>Payment Method:</strong> •••• •••• •••• {subscription?.lastFourDigits}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Premium since {formatDate(subscription?.startDate)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onManage}
                iconName="Settings"
                iconPosition="left"
              >
                Manage
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon name="Star" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-3 text-lg">
                    Unlock Premium Practitioner Features
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-2 mb-6">
                    <li className="flex items-center">
                      <Icon name="Check" size={16} className="mr-3 text-primary flex-shrink-0" />
                      Priority placement in search results and categories
                    </li>
                    <li className="flex items-center">
                      <Icon name="Check" size={16} className="mr-3 text-primary flex-shrink-0" />
                      Enhanced business profiles with rich media and detailed descriptions
                    </li>
                    <li className="flex items-center">
                      <Icon name="Check" size={16} className="mr-3 text-primary flex-shrink-0" />
                      Advanced analytics and client engagement insights
                    </li>
                    <li className="flex items-center">
                      <Icon name="Check" size={16} className="mr-3 text-primary flex-shrink-0" />
                      Direct messaging with potential clients
                    </li>
                    <li className="flex items-center">
                      <Icon name="Check" size={16} className="mr-3 text-primary flex-shrink-0" />
                      Priority customer support and account management
                    </li>
                    <li className="flex items-center">
                      <Icon name="Check" size={16} className="mr-3 text-primary flex-shrink-0" />
                      Monthly performance reports and marketing insights
                    </li>
                  </ul>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-foreground text-xl">£29.99/month</p>
                      <p className="text-xs text-muted-foreground">
                        Cancel anytime • 14-day free trial
                      </p>
                    </div>
                    <Button
                      variant="default"
                      onClick={() => setShowPaymentModal(true)}
                      iconName="CreditCard"
                      iconPosition="left"
                      size="lg"
                    >
                      Start Free Trial
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-muted/50 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-foreground mb-1">Current Plan: Basic</h5>
                  <p className="text-sm text-muted-foreground">
                    Limited visibility and basic listing features
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-foreground">Free</p>
                  <p className="text-xs text-muted-foreground">Always free</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal - Updated for practitioners */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isProcessing && setShowPaymentModal(false)}
          />
          <div className="relative w-full max-w-md bg-popover rounded-lg shadow-organic-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">
                  Start Your Premium Trial
                </h2>
                {!isProcessing && (
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-gentle"
                  >
                    <Icon name="X" size={20} />
                  </button>
                )}
              </div>

              <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground">Premium Practitioner Plan</span>
                  <span className="font-semibold text-primary">£29.99/month</span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  14-day free trial • Cancel anytime
                </p>
                <div className="text-xs text-accent">
                  <Icon name="Info" size={12} className="inline mr-1" />
                  You won't be charged until your trial ends
                </div>
              </div>

              <form onSubmit={handleUpgrade} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    name="cardholderName"
                    value={paymentData?.cardholderName}
                    onChange={handleInputChange}
                    required
                    disabled={isProcessing}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-gentle disabled:opacity-50"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Card Number
                  </label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={paymentData?.cardNumber}
                    onChange={handleInputChange}
                    required
                    disabled={isProcessing}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-gentle disabled:opacity-50"
                    placeholder="1234 5678 9012 3456"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Expiry Date
                    </label>
                    <input
                      type="text"
                      name="expiryDate"
                      value={paymentData?.expiryDate}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-gentle disabled:opacity-50"
                      placeholder="MM/YY"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      CVV
                    </label>
                    <input
                      type="text"
                      name="cvv"
                      value={paymentData?.cvv}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-gentle disabled:opacity-50"
                      placeholder="123"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    loading={isProcessing}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Setting up your trial...' : 'Start 14-Day Free Trial'}
                  </Button>
                </div>
              </form>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Your trial starts today. We'll remind you 3 days before it ends. Cancel anytime from your account settings.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionCard;